import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Send, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import Mascot from "@/components/Mascot";
import { MASCOT } from "@/lib/mascot";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn as cx } from "@/lib/utils";

const SUGGESTIONS_B1 = [
  { cn: "帮我把这句话改得更好：I go school by bus.", en: "Help me improve this sentence: I go school by bus." },
  { cn: "AEIS 阅读怎么快速找到答案？", en: "How can I find answers faster in AEIS reading?" },
  { cn: "在什么时候用 have been 和 went？", en: "When do I use 'have been' vs 'went'?" },
  { cn: "推荐一个我可以练习写作的话题。", en: "Suggest a topic I can practise writing about." },
];

const SUGGESTIONS_A2 = [
  { cn: "'apple' 是什么意思？", en: "What does 'apple' mean?" },
  { cn: "教我三个动物的英文单词。", en: "Teach me three animal words in English." },
  { cn: "'I am'、'you are' 和 'he is' 是什么？", en: "What are 'I am', 'you are', and 'he is'?" },
  { cn: "请出一道简单的英文小测试给我。", en: "Give me a very easy English mini-quiz please." },
];

function splitBilingualReply(text) {
  // Split assistant reply into { cn, en } — Chinese first, English second.
  // Heuristic: find the first line that is majority-latin as the start of English.
  if (!text) return { cn: "", en: "" };
  const lines = text.split(/\n+/);
  const cnLines = [];
  const enLines = [];
  let inEnglish = false;
  for (const line of lines) {
    const stripped = line.trim();
    if (!stripped) continue;
    const hasCJK = /[\u4e00-\u9fff]/.test(stripped);
    const latinChars = (stripped.match(/[A-Za-z]/g) || []).length;
    const isMostlyEnglish = !hasCJK && latinChars > 0;
    if (isMostlyEnglish) inEnglish = true;
    if (inEnglish && isMostlyEnglish) enLines.push(stripped);
    else if (!inEnglish) cnLines.push(stripped);
    else enLines.push(stripped);
  }
  return { cn: cnLines.join(" "), en: enLines.join(" ") };
}

export default function Chat() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const isStudent = user?.role === "student";
  const isA2 = isStudent && user?.level === "A2";
  const suggestions = isA2 ? SUGGESTIONS_A2 : SUGGESTIONS_B1;

  useEffect(() => {
    if (!user) { nav("/auth?mode=login"); return; }
    if (!isStudent) {
      toast.info(
        "Prof. Scaly is here to help students. 小甲教授只为学生开放～",
      );
      nav("/dashboard");
    }
  }, [user, isStudent, nav]);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat/session");
      setMessages(data.messages || []);
      setSessionId(data.id);
    } catch {
      // fall through — start fresh
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isStudent) loadSession(); }, [isStudent, loadSession]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const send = useCallback(async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    setSending(true);
    setInput("");
    // Optimistic append
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: trimmed, created_at: now }]);
    try {
      const { data } = await api.post("/chat/message", {
        text: trimmed, session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, data.reply]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Prof. Scaly is unavailable. Try again in a moment.");
    } finally { setSending(false); }
  }, [input, sessionId]);

  const startNew = useCallback(async () => {
    try {
      const { data } = await api.post("/chat/session/new");
      setSessionId(data.id);
      setMessages([]);
      toast.success("New chat · 新的对话");
    } catch { /* ignore */ }
  }, []);

  const canSend = useMemo(() => input.trim().length > 0 && !sending, [input, sending]);
  const empty = messages.length === 0 && !loading;

  if (!isStudent) return null;

  return (
    <AppLayout>
      <div data-testid="chat-root" className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="card-plush p-6 flex items-center gap-5">
          <Mascot size="sm" />
          <div className="flex-1 min-w-0">
            <span className="badge-soft mb-1">
              {user?.level || "—"} · {isA2 ? "7–9" : "10–12"}
            </span>
            <BilingualText cn={MASCOT.cn} en={MASCOT.en} size="lg" />
            <p className="text-sm text-slate-500 mt-1">
              <span className="cn mr-1">{MASCOT.tagline_cn}</span>· {MASCOT.tagline_en}
            </p>
          </div>
          <Button
            variant="outline"
            className="rounded-full font-bold shrink-0"
            onClick={startNew}
            disabled={sending}
            data-testid="chat-new-session-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            <span className="cn mr-1">新对话</span>New
          </Button>
        </div>

        {/* Messages */}
        <div className="card-plush p-4 sm:p-6 min-h-[420px] max-h-[65vh] overflow-y-auto space-y-5">
          {loading ? (
            <div className="grid place-items-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : empty ? (
            <div className="grid place-items-center py-10">
              <Mascot size="lg" />
              <div className="mt-6 text-center max-w-md">
                <BilingualText
                  cn={`你好，我是${MASCOT.cn}，向我提问吧！`}
                  en={`Hi, I'm ${MASCOT.en}. Ask me anything!`}
                  size="lg"
                  className="items-center"
                />
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 w-full max-w-2xl">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(s.en)}
                    data-testid={`chat-suggestion-${i}`}
                    className="text-left card-plush p-4 hover:border-sky-300 border-2 border-transparent transition-colors"
                  >
                    <div className="cn text-sm font-bold text-slate-900">{s.cn}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.en}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m, i) => (
                <MessageBubble key={i} message={m} index={i} />
              ))}
              {sending && (
                <div className="flex items-start gap-3" data-testid="chat-thinking">
                  <Mascot size="xs" ring={false} />
                  <div className="rounded-2xl px-4 py-3 bg-slate-50 border border-slate-100 text-sm text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="cn">思考中…</span>
                    <span>Thinking…</span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="card-plush p-4 sm:p-5">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (canSend) send();
                  }
                }}
                placeholder="有问题就问小甲吧… Ask Prof. Scaly anything…"
                className="rounded-2xl border-slate-200 focus-visible:ring-emerald-400 resize-none"
                rows={2}
                data-testid="chat-input"
                disabled={sending}
              />
            </div>
            <Button
              size="lg"
              onClick={() => send()}
              disabled={!canSend}
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold h-12 px-6 shrink-0"
              data-testid="chat-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  <span className="cn mr-1">发送</span>Send
                </>
              )}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            <Sparkles className="inline w-3 h-3 mr-1 text-amber-500" />
            <span className="cn mr-1">小甲会用中英双语回答，回答里可能会有小错误哦。</span>
            <span>Prof. Scaly replies in Chinese + English. He may occasionally get things wrong — always double-check.</span>
          </p>
        </div>
      </div>
    </AppLayout>
  );
}

function MessageBubble({ message, index }) {
  const isUser = message.role === "user";
  const parts = isUser ? null : splitBilingualReply(message.content);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.01, 0.1) }}
      className={cx("flex items-start gap-3", isUser && "flex-row-reverse")}
      data-testid={`chat-msg-${index}`}
      data-role={message.role}
    >
      {isUser ? (
        <span className="w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center font-black shrink-0">
          我
        </span>
      ) : (
        <Mascot size="xs" ring={false} />
      )}
      <div
        className={cx(
          "rounded-2xl px-4 py-3 max-w-[75%] shadow-sm",
          isUser
            ? "bg-slate-900 text-white"
            : "bg-white border border-slate-100",
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap text-sm">{message.content}</div>
        ) : (
          <div className="space-y-2">
            {parts.cn && (
              <div className="cn text-slate-900 font-medium leading-relaxed whitespace-pre-wrap">
                {parts.cn}
              </div>
            )}
            {parts.en && (
              <div className="text-slate-600 leading-relaxed whitespace-pre-wrap text-sm">
                {parts.en}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
